import { Fragment } from 'react';
import MojitoPresentation from 'mojito/presentation';
import MojitoServices from 'mojito/services';
import BetViewUtils from 'modules/your-bets/bet-view/utils.js';
import OddsBoostNotification from 'modules/odds-boost-notification/index.jsx';
import MatchAccaSelectionItem from 'modules/match-acca/match-acca-selection-item/index.jsx';

const { FlexPane, Text, Image, ChangedValueIndicator, EarlyPayoutIndicator, DateTime } =
    MojitoPresentation.Components;
const { EventFormatter, OddsUtils, MarketHelper, BettingHelper } = MojitoPresentation.Utils;
const { utils: BetsUtils, types: BetsTypes } = MojitoServices.Bets;
const { PRICE_TYPE, BET_TYPE } = BetsTypes;

const BetLegsDetails = props => {
    const { bet } = props;
    const isMatchAcca = BetsUtils.hasMatchAccaLeg(bet);

    return isMatchAcca ? <MatchAccaInfo {...props} /> : <LegsInfo {...props} />;
};

const MatchAccaInfo = props => {
    const { bet, mojitoTools } = props;
    const { config, stringResolver } = mojitoTools;
    const { legs, status } = bet;
    const { legInfo } = legs[0];
    const { eventName } = legInfo;
    const header = stringResolver.resolveString('$BETSLIP.MATCH_ACCA.BET_HEADER');
    const isOddsBoostBet = BetsUtils.hasOddsBoost(bet);

    return (
        <FlexPane config={config.matchAccaInfo}>
            <LegStatusIcon config={config} status={status} />
            <FlexPane config={config.infoContainerMatchAcca}>
                <FlexPane config={config.selectionContainer}>
                    <FlexPane config={config.selectionNameContainer}>
                        <Text config={config.matchAccaHeaderText}>{header}</Text>
                    </FlexPane>
                    <Odds {...props} leg={bet} />
                </FlexPane>
                <Text config={config.matchAccaEventName}>{eventName}</Text>
                {isOddsBoostBet && (
                    <OddsBoostNotification
                        config={config.matchAccaOddsBoostNotification}
                        bet={bet}
                    />
                )}
                <MatchAccaLegsInfo {...props} />
                <ExtraFields {...props} leg={legs[0]} />
            </FlexPane>
        </FlexPane>
    );
};

const MatchAccaLegsInfo = props =>
    props.bet.legs.map(leg => (
        <MatchAccaLegsItemInfo key={getSelectionId(leg)} {...props} leg={leg} />
    ));

const MatchAccaLegsItemInfo = props => {
    const { leg, mojitoTools } = props;
    const { stringResolver, config } = mojitoTools;
    return leg.parts.map((part, i, row) => {
        const iconStatus = BetViewUtils.getStatusIcon(part.status);
        const showSeparator = i + 1 !== row.length;
        const { selectionName: partSelectionName, hcap = '', marketName } = part;
        const { name, handicapLabel } = MarketHelper.handleHandicap(partSelectionName, hcap);
        const selectionName =
            name || stringResolver.resolveString('$BET_LEGS_DETAILS.VALUE_NOT_AVAILABLE');
        const selectionLabel = `${selectionName} ${handicapLabel}`.trim();
        const isEarlyPayout = !!part?.earlyPayout;

        return (
            <Fragment key={part.selectionId}>
                <MatchAccaSelectionItem
                    config={config.matchAccaSelectionItem}
                    showSeparator={showSeparator}
                    selectionLabel={selectionLabel}
                    marketName={marketName}
                    iconStatus={iconStatus}
                    isEarlyPayout={isEarlyPayout}
                />
            </Fragment>
        );
    });
};

const LegsInfo = props =>
    props.bet.legs.map(leg => <LegsItemInfo key={getSelectionId(leg)} {...props} leg={leg} />);

const LegsItemInfo = props => {
    const { bet, leg, mojitoTools } = props;
    const { config, l10n } = mojitoTools;
    const { legInfo, status, priceType } = leg;
    const { eventStartTime } = legInfo;

    return (
        <FlexPane config={config.legsItemInfo} class="ta-legInfo">
            <LegStatusIcon config={config} status={status} />
            <FlexPane config={config.infoContainer}>
                <FlexPane config={config.selectionContainer}>
                    <Text config={config.selectionName}>
                        {BettingHelper.getSelectionText(leg, l10n)}
                    </Text>
                    <BestOdds priceType={priceType} config={config} />
                    <Odds {...props} />
                </FlexPane>
                <Text config={config.marketName}>
                    {BettingHelper.getMarketNameOrEachWayLabel(leg, l10n, bet.numberOfLines)}
                </Text>
                <Text config={config.eventName} class="ta-eventName">
                    <DateTime
                        config={config.eventNameDateTimeFormat}
                        dateTime={eventStartTime}
                        dateTimeRenderer={formatEventName}
                        rendererData={legInfo.eventName}
                    />
                </Text>
                <BetNotification {...props} />
                <ExtraFields {...props} />
            </FlexPane>
        </FlexPane>
    );
};

const BestOdds = (priceType, config) =>
    priceType === PRICE_TYPE.BP && <Image config={config.bestOddsIcon} class="ta-bestOddsIcon" />;

const Odds = props => {
    const { leg, mojitoTools } = props;
    const { config, l10n } = mojitoTools;
    const { odds, baseOdds, priceType } = leg;
    const oddsLabel = OddsUtils.getDisplayedOdds(odds, priceType, l10n);
    const baseOddsLabel = OddsUtils.getDisplayedOdds(baseOdds, priceType, l10n);

    return baseOdds ? (
        <ChangedValueIndicator
            config={config.priceBoostIndicator}
            previousValue={baseOddsLabel}
            newValue={oddsLabel}
        />
    ) : (
        <Text config={config.oddsLabel} class="ta-priceText">
            {oddsLabel}
        </Text>
    );
};

const BetNotification = props => {
    const { bet, leg, mojitoTools } = props;
    const { config } = mojitoTools;
    const earlyPayoutPart = BetViewUtils.getEarlyPayoutPart(leg);
    const isEarlyPayout = Boolean(earlyPayoutPart);
    const isOddsBoostBet = BetsUtils.hasOddsBoost(bet) && bet.betType === BET_TYPE.SINGLE;

    if (!config.showBetLegNotification) {
        return null;
    }

    return (
        <FlexPane config={config.notificationContainer}>
            {isEarlyPayout && (
                <EarlyPayoutIndicator
                    config={config.earlyPayoutIndicator}
                    awarded={earlyPayoutPart.earlyPayout.awarded}
                />
            )}
            {isOddsBoostBet && (
                <OddsBoostNotification config={config.oddsBoostNotification} bet={bet} />
            )}
        </FlexPane>
    );
};

const LegStatusIcon = ({ status, config }) => {
    const iconStatus = BetViewUtils.getStatusIcon(status);
    return (
        <FlexPane config={config.legStatusIconContainer}>
            <Image config={config.legStatusIcon[iconStatus]} />
        </FlexPane>
    );
};

const ExtraFields = props => {
    const { leg, bet, mojitoTools } = props;
    const { config } = mojitoTools;
    const firstLegPart = BetsUtils.getFirstLegPart(leg);
    const { legInfo } = bet.legs[0];
    const { eventStartTime } = legInfo;

    if (!props.bet.hasOwnProperty('settled')) {
        return null;
    }

    return (
        <FlexPane config={config.extraFields}>
            {config.showLegDateTime && firstLegPart && (
                <FlexPane config={config.legDateTimeContainer} class="ta-legDateTimeContainer">
                    <LegDateTimeInformation
                        mojitoTools={mojitoTools}
                        translation={'$BET_LEGS_DETAILS.STARTED'}
                        dateTime={firstLegPart.started}
                    />
                    <LegDateTimeInformation
                        mojitoTools={mojitoTools}
                        translation={'$BET_LEGS_DETAILS.ENDED'}
                        dateTime={firstLegPart.ended}
                    />
                    <LegDateTimeInformation
                        mojitoTools={mojitoTools}
                        translation={'$BET_LEGS_DETAILS.CONFIRMED'}
                        dateTime={firstLegPart.completed}
                    />
                </FlexPane>
            )}
            {config.showEventStartDateTime && (
                <Text config={config.eventStartDateTimeText} class="ta-eventStartDateTime">
                    <DateTime config={config.eventStartDateTimeFormat} dateTime={eventStartTime} />
                </Text>
            )}
        </FlexPane>
    );
};

const LegDateTimeInformation = ({ translation, dateTime, mojitoTools }) => {
    const { config, stringResolver } = mojitoTools;
    const label = stringResolver.resolveString(translation);
    return (
        <Text config={config.legDateInfoText}>
            {label}
            {!!dateTime.length && (
                <DateTime config={config.legDateTimeFormat} dateTime={dateTime} />
            )}
        </Text>
    );
};

const getSelectionId = leg => BetsUtils.getFirstLegPart(leg)?.selectionId;
const formatEventName = (startTime, eventName) =>
    EventFormatter.formatEventName(eventName, startTime);

export default BetLegsDetails;
